import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { setAuthToken, getAuthToken } from '@/lib/api';
import { setDisplayCurrency } from '@/lib/currency';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track last successful auth check to throttle visibility re-checks
  const lastCheckRef = useRef(0);

  const applyUser = (u) => {
    setUser(u);
    if (u && u.base_currency) setDisplayCurrency(u.base_currency);
  };

  const checkAuth = useCallback(async () => {
    // HttpOnly cookies are sent automatically - no need to check getAuthToken()
    // which returns null since cookies aren't accessible from JS
    try {
      const { data } = await api.get('/api/auth/me');
      lastCheckRef.current = Date.now();
      applyUser(data);
      // v1.4 §1 — Auto-detect currency on first login (when country_code is unset)
      if (data && !data.country_code) {
        try {
          const det = await api.get('/api/auth/detect-location');
          if (det?.data?.currency && det?.data?.country_code) {
            const patched = await api.patch('/api/auth/me', {
              base_currency: det.data.currency,
              country_code: det.data.country_code,
            });
            applyUser(patched.data);
          }
        } catch (err) { console.warn('[auth] detect-location failed (non-blocking):', err?.message || err); }
      }
    } catch (err) {
      console.warn('[auth] /me check failed; clearing session:', err?.message || err);
      // Cookies will be cleared by backend on next failed auth attempt
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-check entitlement when tab regains focus (catches trial expiry while browser is open)
  // Throttled to once per 5 minutes to avoid hammering the backend
  useEffect(() => {
    const THROTTLE_MS = 5 * 60 * 1000; // 5 minutes

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCheckRef.current < THROTTLE_MS) return;
      await checkAuth();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAuth]);

  // Re-check entitlement when a Pro-required 403 is received anywhere in the app
  useEffect(() => {
    const handleProRequired = () => {
      lastCheckRef.current = 0; // Force immediate re-check
      checkAuth();
    };
    window.addEventListener('pro-access-required', handleProRequired);
    return () => window.removeEventListener('pro-access-required', handleProRequired);
  }, [checkAuth]);

  const login = async (username, password, rememberMe = false) => {
    const { data } = await api.post('/api/auth/login', { username, password, remember_me: rememberMe });
    // Cookies are set automatically by the backend via Set-Cookie header
    // No need to call setAuthToken - cookies are HttpOnly and managed by browser
    applyUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (err) { console.warn('[auth] logout call failed:', err?.message || err); }
    // Clear session storage flags on logout
    sessionStorage.removeItem('welcomeShownThisSession');
    // Cookies are cleared by the backend via Set-Cookie with expired date
    setUser(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // After password change, re-login to get fresh token
    setUser((prev) => prev ? { ...prev, must_change_password: false } : prev);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, changePassword, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
